import { NavigationButtons } from '@onegov/react-component-library';
import {
  Button,
  Col,
  ComponentLoader,
  ErrorSummary,
  H3,
  HorizontalRule,
  InPageAlert,
  MoreInfoModal,
  Row,
} from '@snsw/react-component-library';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PagePath } from 'shared/constants/pagePath';
import { useDispatch, useSelector } from 'react-redux';
import { GroupCheckboxCard } from './GroupCheckboxCard';
import { RowContent } from './CheckboxCard';
import styles from '../Page.module.scss';
import {
  filterRenewalItemsGroupAndSort,
  getInfoCodeValue,
  getItemConditionList,
  getItemType,
  getItemTypeDescription,
  getOtherItemType,
  getStringDate,
  sortItemTypeDescription,
} from 'plantItemRenewal/utils/PlantItemRenewalHelpers';
import { useConfirmRemovalDialog } from './confirmRemovalDialog';
import { selectForRenewal, unselectForRenewal } from 'plantItemRenewal/redux/renewalItemsSlice';
import { PlantItemRenewalReviewItemPage } from 'plantItemRenewal/constants/plantItemRenewalReviewItemPage';
import { RenewalDetails } from './RenewalDetails';
import { ItemCertificates } from './ItemCertificates';
import { LoadingStates } from 'shared/constants/loadingStates';
import { RefData } from 'plantItemNew/constants/refData';
import _ from 'lodash';
import { InfoCode } from 'shared/constants/InfoCode';
import { Tangible } from 'shared/constants/Tangible';

export const PlantItemRenewalReviewItems = () => {
  const PAGE_CONSTANTS = PlantItemRenewalReviewItemPage;

  const renewalItems = useSelector((state) => state.renewalItems);
  const refData = useSelector((state) => state.referenceData.data);
  const payment = useSelector((state) => state.plantItemRenewalPayment);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [mustSelectItem, setMustSelectItem] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [errorSummary, setErrorSummary] = useState([]);
  const [currentTangibles, setCurrentTangibles] = useState([]);
  const [expiredTangibles, setExpiredTangibles] = useState([]);

  const onRemoveCurrentTangibleSelection = (rsn) => {
    dispatch(unselectForRenewal(rsn));
  };

  const { openDialog, dialogComponent } = useConfirmRemovalDialog(onRemoveCurrentTangibleSelection);

  const onChangeSelectionCurrentTangible = (item) => {
    if (item.isSelected) {
      //if item currently selected, open confirmation window
      const description = getItemTypeDescription(item.tangibleType, refData);
      const type = getItemType(item);
      openDialog({
        description,
        type,
        location: renewalItems.property[0]?.propAddress,
        registrationNumber: item.tangibleName,
        serialNumber: item.serialNumber,
        rsn: item.tangibleRsn,
      });
    } else {
      dispatch(selectForRenewal(item.tangibleRsn));
    }
  };

  const getNameOfDevice = (item) => {
    if (item.tangibleType != 6) return null;

    return getInfoCodeValue(item, InfoCode.amusementDeviceName);
  };

  const validateItem = (item) => {
    const hasError = false;
    let fieldErrorMessage = '';
    let summaryErrorMessage = '';

    if (item.tangibleType == RefData.plantItemType.amusementDevice) {
      const includeElectricalInstallation = getInfoCodeValue(item, 6051) === RefData.includeElectricalInstallation;
      fieldErrorMessage = 'You must add an inspection certificate';
      summaryErrorMessage = 'Please add an inspection certificate';

      if (includeElectricalInstallation && _.isEmpty(item.electricalCertificates)) {
        return { hasError: true, fieldErrorMessage, summaryErrorMessage };
      }
      if (_.isEmpty(item.engineerCertificates)) {
        return { hasError: true, fieldErrorMessage, summaryErrorMessage };
      }
    }

    return { hasError, fieldErrorMessage, summaryErrorMessage };
  };

  const validateAll = () => {
    setShowErrors(false);
    const selectedItems = renewalItems.items.filter((x) => x.isSelected);
    if (selectedItems.length == 0) {
      setMustSelectItem(true);
      return false;
    } else {
      let valid = true;
      setMustSelectItem(false);
      const errorsForSummary = [];

      const validateTangibles = (tangibles) => {
        return tangibles.map((element) => {
          const newItems = element.items.map((item) => {
            if (selectedItems.find((x) => x.tangibleRsn == item.tangibleRsn)) {
              const { hasError, fieldErrorMessage, summaryErrorMessage } = validateItem(item);
              if (hasError) {
                errorsForSummary.push({ id: item.tangibleRsn, text: summaryErrorMessage });
              }
              valid = valid && !hasError;
              return { ...item, hasError, errorMessage: fieldErrorMessage };
            } else {
              return item;
            }
          });

          return { ...element, items: newItems };
        });
      };

      const newCurrentTangibles = validateTangibles(currentTangibles);
      const newExpiredTangibles = validateTangibles(expiredTangibles);

      setCurrentTangibles(newCurrentTangibles);
      setExpiredTangibles(newExpiredTangibles);
      setErrorSummary(errorsForSummary);
      setShowErrors(!valid);

      return valid;
    }
  };

  const onContinueClick = () => {
    if (validateAll()) {
      navigate(PagePath.plantItemRenewalContactDetails);
    } else {
      window.scrollTo(0, 0);
    }
  };

  const onClickLookup = () => {
    navigate(PagePath.plantItemRenewalFind);
  };

  const onAddCertificateClick = (item) => {
    navigate(PagePath.plantItemRenewalAddCertificates, { state: { item } });
  };

  const onClickEditItem = (item) => {
    const address = renewalItems.property[0]?.propAddress;
    navigate(PagePath.plantItemRenewalEditItem, { state: { item, address } });
  };

  const editItemLabel = (label, item) => {
    return (
      <span>
        {label}{' '}
        <Button
          variant="link"
          onClick={() => {
            onClickEditItem(item);
          }}
        >
          Edit
        </Button>
      </span>
    );
  };

  const defaultContent = (item) => {
    const itemType = getItemType(item);
    return (
      <>
        {itemType && <RowContent label={PAGE_CONSTANTS.itemTypeLabel} value={itemType} />}

        <RowContent label={PAGE_CONSTANTS.itemSerialNumberLabel} value={item.serialNumber} />
        <RowContent
          label={PAGE_CONSTANTS.itemSpecificLocationLabel}
          value={editItemLabel(getInfoCodeValue(item, InfoCode.specificLocation), item)}
        />
        <RowContent
          label={PAGE_CONSTANTS.itemUniqueIdLabel}
          value={editItemLabel(getInfoCodeValue(item, InfoCode.customersUid), item)}
        />

        <ItemCertificates
          item={item}
          onAddCertificate={() => {
            onAddCertificateClick(item);
          }}
        />
      </>
    );
  };

  const expandedContent = (item) => {
    const nameOfDevice = getNameOfDevice(item);
    const otherTypeDesc = getOtherItemType(item);
    return (
      <>
        {nameOfDevice && <RowContent label={PAGE_CONSTANTS.itemNameOfDeviceLabel} value={nameOfDevice} />}
        {otherTypeDesc && <RowContent label={PAGE_CONSTANTS.itemOtherTypeDescLabel} value={otherTypeDesc} />}
        <RowContent label={PAGE_CONSTANTS.itemModelNameOrNumberLabel} value={item.modelNumber} />
        <RowContent
          label={PAGE_CONSTANTS.itemExpiryDateLabel}
          value={getStringDate(getInfoCodeValue(item, InfoCode.expiryDate))}
        />
        {getItemConditionList(item).map((condition, i) => {
          return (
            <>
              {i === 0 && (
                <RowContent
                  label={i === 0 && PAGE_CONSTANTS.itemConditionsLabel}
                  value={''}
                  key={`itemConditionLabel${item.tangibleRsn}`}
                />
              )}
              <RowContent label={''} value={condition} key={`itemCondition${item.tangibleRsn}${i}`} />
            </>
          );
        })}
      </>
    );
  };

  const sortedItemTypeDescription = sortItemTypeDescription(refData);

  useEffect(() => {
    const tangiblesCurrent = filterRenewalItemsGroupAndSort(
      sortedItemTypeDescription,
      renewalItems.items,
      Tangible.currentTangible,
    );
    const tangiblesExpired = filterRenewalItemsGroupAndSort(
      sortedItemTypeDescription,
      renewalItems.items,
      Tangible.expiredTangible,
    );
    setCurrentTangibles(tangiblesCurrent);
    setExpiredTangibles(tangiblesExpired);
  }, [renewalItems.items]);

  return (
    <>
      <Row>
        <Col span={8}>
          {showErrors && !!errorSummary?.length && (
            <ErrorSummary
              singularTitle="We need more information"
              pluralTitle="We need more information"
              errors={errorSummary}
            />
          )}
          {mustSelectItem && (
            <InPageAlert variant="error" title="You must renew at least one item">
              Select one or more current or expired items to continue.
            </InPageAlert>
          )}
          <RenewalDetails renewalItems={renewalItems} onClickLookup={onClickLookup} />
          {currentTangibles.length > 0 && (
            <>
              <H3>Current items</H3>
              <p>If you deselect an item, you’ll need to reapply for registration before you can use it again.</p>
            </>
          )}
          {currentTangibles.map((element, index) => (
            <GroupCheckboxCard
              key={index}
              id={`current_${element.code}`}
              items={element.items}
              title={`${element.items?.length} ${element.desc}`}
              getDefaultContent={defaultContent}
              getExpandedContent={expandedContent}
              getCardTitle={(item) => {
                return item.tangibleName;
              }}
              onChange={onChangeSelectionCurrentTangible}
              isChecked={(item) => item.isSelected}
              getId={(item) => item.tangibleRsn}
            />
          ))}
          {expiredTangibles.length > 0 && (
            <>
              <H3 className={styles.marginTopHalfRem}>Expired items</H3>
              <p>If you would like to use any of these expired items, select to add them to this renewal.</p>
            </>
          )}
          {expiredTangibles.map((element, index) => (
            <GroupCheckboxCard
              key={index}
              id={`current_${element.code}`}
              items={element.items}
              title={`${element.items?.length} ${element.desc}`}
              getDefaultContent={defaultContent}
              getExpandedContent={expandedContent}
              getCardTitle={(item) => {
                return item.tangibleName;
              }}
              onChange={onChangeSelectionCurrentTangible}
              isChecked={(item) => item.isSelected}
            />
          ))}
          {dialogComponent}
        </Col>
      </Row>

      <p>
        <strong>
          <span style={{ paddingRight: 8 }}>I can&rsquo;t see all my items</span>
          <MoreInfoModal
            helpText={
              <div style={{ fontWeight: 300 }}>
                If you cannot see all items at a plant location in this renewal, here are some possible reasons:
                <ul>
                  <li>Your items have been expired for more than 12 months.</li>
                  <li>You’ve recently applied for registration and are still waiting for approval.</li>
                  <li>The location address is different in our records.</li>
                </ul>
              </div>
            }
            // questionIconFlag
            subTitle=""
            title="I can’t see all my items"
          />
        </strong>
      </p>

      <HorizontalRule />
      <NavigationButtons
        leftButtonConfig={{
          label: 'Continue',
          onClick: onContinueClick,
          isVisible: !payment.paymentReference,
        }}
        rightButtonConfig={{
          isVisible: false,
        }}
      />
      {renewalItems.status === LoadingStates.Loading && (
        <Row>
          <Col span={12}>
            <ComponentLoader fullPage />
          </Col>
        </Row>
      )}
    </>
  );
};
